import {
  channelDetailsPath,
  channelTabPath,
  channelTabWireParams,
  err,
  ok,
  parseChannelDetails,
  parseChannelPlaylistPage,
  parseChannelVideoPage,
  parseFeedItems,
  resolveInstanceUrl,
  withQuery,
  type AbortLike,
  type ChannelDetails,
  type ChannelListTab,
  type ChannelPage,
  type ChannelVideoSort,
  type FeedItem,
  type ParsedList,
  type PlaylistSummary,
  type Result,
  type VideoSummary,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { fetchJson } from './json';
import type { ApiProblem } from './videos';

/**
 * §8's Channel row. Five endpoints and **two** envelope shapes, which is the thing to keep straight
 * after 2b and 2c: `/api/v1/channels/{ucid}` is a bare record, the three video tabs answer
 * `{videos:[…],continuation?}` and the playlists tab answers `{playlists:[…],continuation?}`.
 *
 * Every parameter decision is in `channel/tabs.ts` — which sort may be sent, and to which tab. This
 * file is the request plumbing and nothing else.
 *
 * The bodies are read through `api/json.ts` since 4a. The private reader it replaces caught
 * `JSON.parse`'s throw and reported `malformed-json` — whose copy points the reader at a proxy — for a
 * case its own comment recorded as real: `/api/v1/channels/{ucid}/notatab` answers **200 with zero
 * bytes**. So a mistyped sub-path now says the instance answered with nothing, which is what happened.
 */
export type ChannelDetailsResult = Result<ChannelDetails, ApiProblem>;
export type ChannelVideoPageResult = Result<ChannelPage<VideoSummary>, ApiProblem>;
export type ChannelPlaylistPageResult = Result<ChannelPage<PlaylistSummary>, ApiProblem>;

export async function fetchChannelDetails(
  platform: Platform,
  channelId: string,
  signal?: AbortLike,
): Promise<ChannelDetailsResult> {
  const path = channelDetailsPath(channelId);
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseChannelDetails(body.value, platform.parseContext);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/**
 * One page of `/videos`, `/shorts` or `/streams`.
 *
 * `continuation` is the paging cursor and it is **not** in the URL, unlike search's `page=`. The token
 * is ~4 KB of opaque base64 per page: putting it in the address bar would make every "load more" a
 * navigation carrying a blob nobody can read, and it would not even be shareable — a spent token
 * answers `500` rather than replaying its page. So the tab pages in memory and the *route* carries
 * only the tab and the sort, which are the two things a reader would want to link to.
 */
export async function fetchChannelVideoPage(
  platform: Platform,
  channelId: string,
  tab: Exclude<ChannelListTab, 'playlists'>,
  sort: ChannelVideoSort,
  continuation: string | null,
  signal?: AbortLike,
): Promise<ChannelVideoPageResult> {
  const raw = await fetchTab(platform, channelId, tab, sort, continuation, signal);
  if (!raw.ok) return err(raw.error);
  return pageOrParseFailure(parseChannelVideoPage(raw.value, platform.parseContext));
}

export async function fetchChannelPlaylistPage(
  platform: Platform,
  channelId: string,
  sort: ChannelVideoSort,
  continuation: string | null,
  signal?: AbortLike,
): Promise<ChannelPlaylistPageResult> {
  const raw = await fetchTab(platform, channelId, 'playlists', sort, continuation, signal);
  if (!raw.ok) return err(raw.error);
  return pageOrParseFailure(parseChannelPlaylistPage(raw.value, platform.parseContext));
}

/**
 * One page of `/api/v1/channels/{ucid}/search` — the in-channel search.
 *
 * A third envelope for this file's list: a **bare array**, `/search`'s own, mixing videos and
 * playlists — so it goes through `parseFeedItems` like a search page does. It takes `q` and `page=`
 * and nothing else, because the endpoint honours nothing else: every filter is *ignored* here
 * (§7f — the one measured mercy, next to the `channel:` operator which corrupts instead).
 */
export type ChannelSearchResult = Result<ParsedList<FeedItem>, ApiProblem>;

export async function fetchChannelSearch(
  platform: Platform,
  channelId: string,
  q: string,
  page: number,
  signal?: AbortLike,
): Promise<ChannelSearchResult> {
  const path = `/api/v1/channels/${encodeURIComponent(channelId)}/search`;
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, { q, page: page > 1 ? String(page) : null });
  if (url === null) return err({ kind: 'bad-url', path });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseFeedItems(body.value, platform.parseContext);
  // `api/search.ts`'s envelope reading: an object where the bare array belongs is a parse failure,
  // not an empty result set.
  if (parsed.items.length === 0 && parsed.dropped.length === 1) {
    const error = parsed.dropped[0];
    if (error !== undefined && error.reason === 'wrong-type' && error.field === null) {
      return err({ kind: 'parse', error });
    }
  }
  return ok(parsed);
}

async function fetchTab(
  platform: Platform,
  channelId: string,
  tab: ChannelListTab,
  sort: ChannelVideoSort,
  continuation: string | null,
  signal?: AbortLike,
): Promise<Result<unknown, ApiProblem>> {
  const path = channelTabPath(channelId, tab);
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, channelTabWireParams(tab, sort, continuation));
  if (url === null) return err({ kind: 'bad-url', path });

  return await fetchJson(platform, url, signal);
}

/**
 * The same reading `api/feeds.ts` and `api/search.ts` make, one level in: a response whose *envelope*
 * was wrong drops one error and no items, and that is a parse failure rather than an empty tab. Rows
 * that dropped themselves are not — R4 says those are counted and shown.
 *
 * It matters more here than on a feed, because a mistyped sub-path answers **`200` with zero bytes** —
 * so "this tab is empty" and "that is not a tab" are two states the API hands over as one status.
 */
function pageOrParseFailure<T>(page: ChannelPage<T>): Result<ChannelPage<T>, ApiProblem> {
  if (page.items.length === 0 && page.dropped.length === 1) {
    const error = page.dropped[0];
    if (error !== undefined && error.reason === 'wrong-type') return err({ kind: 'parse', error });
  }
  return ok(page);
}
